import { useEffect, useRef } from "react";

/**
 * ColorInvoice-style cursor:
 * - One solid dot following pointer
 * - 20 trail dots fading in size + opacity, alternating electric blue / gold
 * Hover targets pulse the lead dot slightly larger.
 */
const TRAIL = 20;
const COLORS = ["#1E6FFF", "#F5C842"]; // alternate blue / gold

export default function CustomCursor() {
  const dotRef = useRef(null);
  const trailRefs = useRef([]);

  useEffect(() => {
    const points = Array.from({ length: TRAIL }, () => ({ x: -100, y: -100 }));
    let mouseX = -100;
    let mouseY = -100;
    let raf;

    const onMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };

    const interactiveSelector =
      'a, button, [role="button"], input, textarea, select, label, [data-cursor="hover"]';

    const onOver = (e) => {
      if (e.target.closest && e.target.closest(interactiveSelector)) {
        if (dotRef.current) dotRef.current.classList.add("is-hovering");
      }
    };
    const onOut = (e) => {
      if (e.target.closest && e.target.closest(interactiveSelector)) {
        if (dotRef.current) dotRef.current.classList.remove("is-hovering");
      }
    };
    const onDown = () => {
      if (dotRef.current) dotRef.current.classList.add("is-clicking");
    };
    const onUp = () => {
      if (dotRef.current) dotRef.current.classList.remove("is-clicking");
    };

    const tick = () => {
      // Lead dot — slight lerp for ultra smooth feel
      points[0].x += (mouseX - points[0].x) * 0.6;
      points[0].y += (mouseY - points[0].y) * 0.6;

      // Trail follows previous point
      for (let i = 1; i < TRAIL; i++) {
        points[i].x += (points[i - 1].x - points[i].x) * 0.32;
        points[i].y += (points[i - 1].y - points[i].y) * 0.32;
      }

      // Apply transforms
      const dot = dotRef.current;
      if (dot) dot.style.transform = `translate3d(${points[0].x - 6}px, ${points[0].y - 6}px, 0)`;

      for (let i = 0; i < TRAIL; i++) {
        const el = trailRefs.current[i];
        if (!el) continue;
        const size = 12 - (i * 0.5); // 12 -> 2.5
        const half = size / 2;
        el.style.width = `${size}px`;
        el.style.height = `${size}px`;
        el.style.transform = `translate3d(${points[i].x - half}px, ${points[i].y - half}px, 0)`;
      }

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    window.addEventListener("mousemove", onMove);
    document.addEventListener("mouseover", onOver, true);
    document.addEventListener("mouseout", onOut, true);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver, true);
      document.removeEventListener("mouseout", onOut, true);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  return (
    <>
      <div
        id="cursor-dot"
        ref={dotRef}
        aria-hidden="true"
        data-testid="cursor-dot"
      />
      {Array.from({ length: TRAIL }).map((_, i) => (
        <div
          key={i}
          ref={(el) => (trailRefs.current[i] = el)}
          className="cursor-trail-dot"
          aria-hidden="true"
          style={{
            backgroundColor: COLORS[i % 2],
            opacity: Math.max(0.18, 1 - i * 0.04),
          }}
        />
      ))}
    </>
  );
}
